import { useMemo } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useAccess } from "@/lib/access";
import { AlertOctagon } from "lucide-react";

/**
 * RemindersRedirect — 149-cq PROMPT 16-R2A.
 * Menggantikan `<Navigate to="/info-agenda?tab=reminder" replace/>` yang buta permission.
 *
 * Aturan redirect:
 *   • reminders.view=true  → /info-agenda?tab=reminder (prioritaskan reminder karena URL asli /reminders)
 *   • announcements.view=true (dan reminders.view=false) → /info-agenda?tab=pengumuman
 *   • keduanya OFF → tampilkan "Akses Ditolak" (pesan menyebut permission spesifik).
 *
 * TIDAK melakukan fetch data reminder untuk user tanpa reminders.view (default lint-safe:
 * Navigate langsung dieksekusi, komponen `RemindersPage` tidak di-mount).
 */
export default function RemindersRedirect() {
  const { user } = useAuth();
  const { canPerm, ready } = useAccess();
  const location = useLocation();

  const target = useMemo(() => {
    if (!user || !ready) return null;
    const canRem = canPerm(user, "reminders", "view");
    const canAnn = canPerm(user, "announcements", "view");
    // Teruskan query params asli (mis. ?open=<id> deep-link) ke tab target.
    const params = new URLSearchParams(location.search);
    if (canRem) { params.set("tab", "reminder"); return `/info-agenda?${params.toString()}`; }
    if (canAnn) { params.set("tab", "pengumuman"); return `/info-agenda?${params.toString()}`; }
    return "__denied__";
  }, [user, ready, canPerm, location.search]);

  if (!ready) return null;
  if (target === "__denied__") {
    return (
      <div className="p-6" data-testid="reminders-legacy-denied">
        <p className="overline text-slate-500">Komunikasi & Agenda</p>
        <h1 className="font-display text-3xl tracking-tight text-[#0B1D3A] mb-4">Reminder &amp; Event</h1>
        <div className="border border-rose-200 bg-rose-50 p-8 text-center">
          <AlertOctagon size={28} className="mx-auto text-rose-500 mb-2" />
          <h2 className="font-display text-xl text-rose-900 tracking-tight">Akses Ditolak</h2>
          <p className="text-sm text-rose-700 mt-2 max-w-lg mx-auto">
            Anda tidak memiliki akses <b>Reminder View</b> maupun <b>Announcements View</b>.
            Hubungi admin untuk konfigurasi Access Control Matrix.
          </p>
        </div>
      </div>
    );
  }
  if (target) return <Navigate to={target} replace />;
  return null;
}
